import { React, useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchWithTimeout } from "../../hooks/useFetchWithTimeout";
import { GetStorageLink } from "../../BackendAccess/FrontendHelpers";
import PageWrapper from "../pageWrapper";

// CSS
import "./warehouseEditAndCreatePage.css";
import "../0PageStyles.css";

// Components
import Button from "../../Components/Other Components/button";
import InputField from "../../Components/Other Components/inputField";
import InputGroup from "../../Components/Other Components/inputGroup";
import PopupConfirmation from "../../Components/Other Components/popupConfirmation";
import Title from "../../Components/Other Components/Title";
import ItemView from "../../Components/Item Components/itemView";
import PageLoader from "../../Components/Other Components/pageLoader";
import MandatoryExplanation from "../../Components/Other Components/mandatoryExplanation";

// Functions and hooks
import {
  EditWarehouse,
  DeleteWarehouse,
  GetWarehouseData,
  CheckForWarehouse,
} from "../../BackendAccess/WarehouseHooks";
import { useNotification } from "../../Contexts/NotificationContext";
import { useErrorHandler } from "../../hooks/errorHandler";
import useFormState from "../../hooks/useFormStates";
import { usePopup } from "../../Contexts/PopupContext";
import { useTranslation } from "react-i18next";

export default function WarehouseEditPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useNotification();
  const currentID = location.state?.id;

  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [pageError, setPageError] = useState(null);
  const [error, setError] = useState(null);
  const [deleteBtnDisabled, setDeleteBtnDisabled] = useState(false);
  const [canSaveChanges, setCanSaveChanges] = useState(false);

  const { formData, setField, loadInitialData, hasChanged } = useFormState({
    Name: "",
    Address: "",
    WarehouseID: "",
  });

  useErrorHandler(error, showNotification, setPageError);

  const { showPopup, removePopup } = usePopup();

  const { t } = useTranslation();

  const openDeletePopup = () => {
    const key = Date.now();
    showPopup(
      <PopupConfirmation
        style={"warning"}
        message={
          <p>
            {t("Are you sure you want to delete this warehouse?", {
              ns: "warehouse",
            })}
            <br />{" "}
            {t(
              "All traffic notes attached to this warehouse will be deleted!",
              { ns: "warehouse" }
            )}
            <br />
            <span className="text-negative">
              {t("This action cannot be undone.", { ns: "warehouse" })}
            </span>
          </p>
        }
        onConfirm={() => handleConfirmDelete(key)}
        onCancel={() => handleCancelDelete(key)}
      />,
      key
    );
  };

  const imageUrl = useMemo(
    () =>
      preview
        ? preview.startsWith("data:")
          ? preview
          : GetStorageLink() + preview
        : null,
    [preview]
  );

  const checkIfWarehouseExists = useCallback(
    async (id) => {
      setLoading(true);
      try {
        const [bWarehouseExists, checkError] = await CheckForWarehouse(id);
        if (checkError) {
          setError(checkError);
          navigate(-1);
        }
      } catch (err) {
        setError({
          errCode: 404,
          err: t("warehouse.Warehouse not found.", { ns: "error" }),
        });
      }
    },
    [navigate, setError]
  );

  // Load warehouse data
  const getAndLoadData = useCallback(async () => {
    try {
      const [data, error] = await fetchWithTimeout(() =>
        GetWarehouseData(currentID)
      );
      if (error) {
        error.isPageError = true;
        setError(error);
      } else {
        loadData(data);
      }
    } catch (err) {
      setError({
        errCode: 500,
        err: t("server.Server error", { ns: "error" }),
      });
    } finally {
      setLoading(false);
    }
  }, [currentID, setError]);

  const loadData = useCallback(
    (items) => {
      setPreview(items.ImgPath || null);
      loadInitialData({
        Name: items.Name,
        WarehouseID: items.WarehouseID.toString(),
        Address: items.Address,
        LastEditedBy:
          items.FirstName +
          " " +
          items.LastName +
          " (" +
          items.LastEditedBy +
          ")",
      });
    },
    [loadInitialData]
  );

  // Input handlers
  const handleInputChange = useCallback(
    (field, value) => {
      setField(field, value);
    },
    [setField, setError]
  );

  const handleImageChange = useCallback(
    (file) => {
      setImage(file);
      if (file) {
        setImageLoading(true);
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result);
          setImageLoading(false);
        };
        reader.onerror = () => {
          setError({
            err: t("Failed to load image.", { ns: "error" }),
            errCode: 400,
          });
          setImageLoading(false);
        };
        reader.readAsDataURL(file);
      } else {
        setPreview(null);
        setImageLoading(false);
      }
    },
    [setError]
  );

  // Action handlers
  const clickOnCancel = useCallback(() => {
    showNotification(
      t("Warehouse editing canceled.", { ns: "warehouse" }),
      "info"
    );
    navigate(-1);
  }, [navigate, showNotification]);

  const clickOnDelete = useCallback(() => {
    openDeletePopup();
  }, []);

  const clickOnCreateNote = useCallback(() => {
    navigate("/workspace/traffic/noteCreationPage", {
      state: { WarehouseID: currentID },
    });
  }, [currentID, navigate]);

  const handleConfirmDelete = useCallback(
    async (key) => {
      setDeleteBtnDisabled(true);
      removePopup(key);
      showNotification(t("Deleting warehouse...", { ns: "warehouse" }), "info");
      try {
        const [result, deleteError] = await DeleteWarehouse(currentID);
        if (deleteError === null) {
          showNotification(
            t("Warehouse deleted successfully!", { ns: "warehouse" }),
            "success"
          );
          navigate(-1);
        } else {
          setError(deleteError);
        }
      } catch (err) {
        setError({
          err: t("server.Server error.", { ns: "error" }),
          errCode: 500,
        });
      } finally {
        setDeleteBtnDisabled(false);
      }
    },
    [currentID, navigate, showNotification, setError]
  );

  const handleCancelDelete = useCallback((key) => {
    removePopup(key);
  }, []);

  const onConfirmChanges = useCallback(async () => {
    try {
      showNotification(
        t("Warehouse editing in progress...", { ns: "warehouse" }),
        "info"
      );
      setCanSaveChanges(false);
      const [result, editError] = await EditWarehouse(
        image,
        formData.Name,
        formData.WarehouseID,
        formData.Address,
        currentID
      );
      if (editError === null) {
        showNotification(
          t("Warehouse edited successfully!", { ns: "warehouse" }),
          "success"
        );
        navigate(-1);
      } else setError(editError);
    } catch (err) {
      setError({
        err: t("server.Server error.", { ns: "error" }),
        errCode: 500,
      });
    } finally {
      setCanSaveChanges(true);
    }
  }, [image, formData, currentID, navigate, showNotification, setError]);

  // Effects
  useEffect(() => {
    if (currentID) {
      checkIfWarehouseExists(currentID);
      getAndLoadData();
    }
  }, [currentID, checkIfWarehouseExists, getAndLoadData]);

  useEffect(() => {
    setCanSaveChanges(hasChanged || image !== null);
  }, [formData, image, hasChanged]);

  // Memoized components and UI elements
  const editFormSection = useMemo(
    () => (
      <InputGroup className="container-empty vertical warehouse-edit">
        <InputField
          className="mb-2"
          placeholder={t("Example: 1001")}
          onChange={(e) => handleInputChange("WarehouseID", e)}
          label="ID:"
          value={formData.WarehouseID}
          mandatory={true}
          description={t("The custom integer ID of your warehouse", {
            ns: "warehouse",
          })}
          readOnly={true}
          tutorialId="warehouse-edit-id"
        />
        <InputField
          className="mb-2"
          placeholder={t("Example: Warehouse1", { ns: "warehouse" })}
          onChange={(e) => handleInputChange("Name", e)}
          label={t("Name") + ":"}
          value={formData.Name}
          mandatory={true}
          description={t("The name of your warehouse", { ns: "warehouse" })}
          tutorialId="warehouse-edit-name"
        />
        <InputField
          placeholder={t("Example: City1, Street23 15", { ns: "warehouse" })}
          onChange={(e) => handleInputChange("Address", e)}
          label={t("Address") + ":"}
          value={formData.Address}
          description={t("The address of your warehouse", { ns: "warehouse" })}
          tutorialId="warehouse-edit-address"
        />
        <MandatoryExplanation />
      </InputGroup>
    ),
    [formData, handleInputChange]
  );

  const actionButtons = useMemo(
    () => (
      <div className="btn-container m-4">
        <Button
          btnStyle={"btn-positive"}
          onClick={onConfirmChanges}
          acceptEnter={true}
          disabled={!canSaveChanges || imageLoading}
          icon={"apply"}
          tutorialId="warehouse-edit-apply-btn"
        >
          {t("Apply Changes")}
        </Button>
        <Button
          btnStyle="btn-primary"
          onClick={clickOnCancel}
          disabled={imageLoading}
        >
          {t("Cancel")}
        </Button>
        <Button
          btnStyle="btn-negative"
          onClick={clickOnDelete}
          disabled={deleteBtnDisabled || imageLoading}
          icon={"delete"}
          tutorialId="warehouse-edit-delete-btn"
        >
          {t("Delete")}
        </Button>
        <Button
          btnStyle="btn-positive"
          onClick={clickOnCreateNote}
          disabled={imageLoading}
          icon={"create"}
          tutorialId="warehouse-edit-create-note-btn"
        >
          {t("Create note")}
        </Button>
      </div>
    ),
    [
      canSaveChanges,
      imageLoading,
      deleteBtnDisabled,
      onConfirmChanges,
      clickOnCancel,
      clickOnDelete,
      clickOnCreateNote,
    ]
  );

  const itemViewSection = useMemo(
    () => (
      <div className="view-page-container" data-tutorial-id="warehouse-edit-items">
        <ItemView
          Tit={t("Items in this warehouse")}
          bEnableCreate={false}
          bEnableEdit={false}
          bShowAll={false}
          WarehouseID={currentID || -2}
        />
      </div>
    ),
    [currentID, t]
  );

  const tutorialSteps = [
    {
      target: null,
      title: t("Warehouse details", { ns: "tutorial" }),
      message: t(
        "This page shows one warehouse's details and everything currently stocked inside it.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "warehouse-edit-id",
      title: t("Warehouse ID", { ns: "tutorial" }),
      message: t(
        "The warehouse's ID. This is fixed and can't be changed after creation.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "warehouse-edit-name",
      title: t("Name"),
      message: t("Rename the warehouse here if you need to.", {
        ns: "tutorial",
      }),
    },
    {
      target: "warehouse-edit-apply-btn",
      title: t("Apply Changes"),
      message: t(
        "Save any changes you've made to the fields above.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "warehouse-edit-create-note-btn",
      title: t("Create note"),
      message: t(
        "Start a new traffic note (a delivery, received goods, or sale) with this warehouse already selected.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "warehouse-edit-delete-btn",
      title: t("Delete"),
      message: t(
        "Permanently deletes this warehouse. Only possible once it has no stock left in it.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "warehouse-edit-items",
      title: t("Items in this warehouse", { ns: "tutorial" }),
      message: t(
        "Everything currently stocked in this warehouse, with quantities. This is the same item list you know from the Items page, filtered down to just this location.",
        { ns: "tutorial" }
      ),
    },
  ];

  return (
    <PageWrapper tutorialId="warehouseEdit" tutorialSteps={tutorialSteps}>
      <PageLoader loading={loading} error={pageError}>
        <div className="page-content">
          <div className="spacer m"></div>
          <Title>{formData.Name}</Title>
          <div className="container-bordered horizontal mb-2 w-90">
            <div className="container-empty file-upload">
              <PageLoader loading={imageLoading} error={null}>
                <InputField
                  type={"file"}
                  previewImage={imageUrl}
                  onChange={handleImageChange}
                  label={t("Upload Image")}
                />
              </PageLoader>
            </div>
            {editFormSection}
          </div>
          <span className="last-edited-by-bar w-90">
            <span className="sec-textcolor-span">{t("Last edited by:")}</span>
            &nbsp;
            {formData.LastEditedBy}
          </span>

          {actionButtons}
          {itemViewSection}
        </div>
      </PageLoader>
    </PageWrapper>
  );
}
